<template>
  <div class="container">
  <div class="admin-pricing-page">
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="container-fluid p-4" id="main-content">
      <!-- Breadcrumb Navigation -->
      <nav aria-label="breadcrumb" class="mb-4">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/admin">
              <font-awesome-icon icon="cog" class="me-1" />
              Admin
            </router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            <font-awesome-icon icon="euro-sign" class="me-1" />
            Preis-Einstellungen
          </li>
        </ol>
      </nav>

      <!-- Page Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0">
          <font-awesome-icon icon="euro-sign" class="me-2" />
          Preis-Einstellungen
        </h1>
        <BackButton fallback="admin" />
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Laden...</span>
        </div>
        <p class="mt-2">Lade Preisregeln...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <font-awesome-icon icon="exclamation-triangle" class="me-2" />
        {{ error }}
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Tabs -->
        <ul class="nav nav-tabs mb-4" id="pricingTabs" role="tablist">
          <li class="nav-item" role="presentation">
            <button 
              class="nav-link active" 
              id="coin-tab" 
              data-bs-toggle="tab" 
              data-bs-target="#coin-content" 
              type="button" 
              role="tab"
              @click="activeTab = 'coin'"
            >
              <font-awesome-icon icon="coins" class="me-2" />
              Quittung-Preise
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button 
              class="nav-link" 
              id="team-tab" 
              data-bs-toggle="tab" 
              data-bs-target="#team-content" 
              type="button" 
              role="tab"
              @click="activeTab = 'team'"
            >
              <font-awesome-icon icon="users" class="me-2" />
              Bereich-Pläne
            </button>
          </li>
        </ul>

        <!-- Tab Content -->
        <div class="tab-content" id="pricingTabContent">
          <!-- Quittung Pricing Tab -->
          <div class="tab-pane fade show active" id="coin-content" role="tabpanel">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <font-awesome-icon icon="coins" class="me-2" />
                  Quittung-Preisregeln
                </h5>
                <button 
                  class="btn btn-primary btn-sm" 
                  @click="showCreateModal('coin_purchase')"
                >
                  <font-awesome-icon icon="plus" class="me-2" />
                  Neue Regel
                </button>
              </div>
              <div class="card-body">
                <div v-if="coinRules.length === 0" class="text-center py-4">
                  <div class="text-muted">
                    <font-awesome-icon icon="coins" class="h1 mb-3" />
                    <p>Noch keine Quittung-Preisregeln definiert</p>
                    <button class="btn btn-primary" @click="showCreateModal('coin_purchase')">
                      Erste Regel erstellen
                    </button>
                  </div>
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>

                        <th>Quittungen</th>
                        <th>Preis pro Quittung</th>
                        <th>Preis-Typ</th>
                        <th>Status</th>
                        <th>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="rule in coinRules" :key="rule.id">
              
                        <td>
                          <span v-if="rule.minCoins && rule.maxCoins">
                            {{ rule.minCoins }} - {{ rule.maxCoins }}
                          </span>
                          <span v-else-if="rule.minCoins">
                            Ab {{ rule.minCoins }}
                          </span>
                          <span v-else-if="rule.maxCoins">
                            Bis {{ rule.maxCoins }}
                          </span>
                          <span v-else>Alle</span>
                        </td>
                        <td>
                          <strong>{{ formatCurrency(rule.pricePerCoin) }}</strong>
                        </td>
                        <td>
                          <span :class="getPriceTypeBadgeClass(rule.priceType)">
                            {{ rule.priceType === 'netto' ? 'Netto' : 'Brutto' }}
                          </span>
                        </td>
                        <td>
                          <span :class="getStatusBadgeClass(rule.isActive)">
                            {{ rule.isActive ? 'Aktiv' : 'Inaktiv' }}
                          </span>
                        </td>
                        <td>
                          <div class="btn-group btn-group-sm">
                            <button 
                              class="btn btn-outline-primary" 
                              @click="editRule(rule)"
                              title="Bearbeiten"
                            >
                              <font-awesome-icon icon="edit" />
                            </button>
                            <button 
                              class="btn btn-outline-danger" 
                              @click="deleteRule(rule)"
                              title="Löschen"
                            >
                              <font-awesome-icon icon="trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Team Plans Tab -->
          <div class="tab-pane fade" id="team-content" role="tabpanel">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <font-awesome-icon icon="users" class="me-2" />
                  Plan Preise
                </h5>
                <div class="btn-group btn-group-sm">
                  <button 
                    class="btn btn-primary" 
                    @click="showCreateModal('team_pro')"
                    :disabled="hasProPlan"
                  >
                    <font-awesome-icon icon="plus" class="me-2" />
                    Pro Plan
                  </button>
                  <button 
                    class="btn btn-primary" 
                    @click="showCreateModal('team_ultimate')"
                    :disabled="hasUltimatePlan"
                  >
                    <font-awesome-icon icon="plus" class="me-2" />
                    Ultimate Plan
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div v-if="teamRules.length === 0" class="text-center py-4">
                  <div class="text-muted">
                    <font-awesome-icon icon="users" class="h1 mb-3" />
                    <p>Noch keine Plan Preise definiert</p>
                    <div class="btn-group">
                      <button class="btn btn-primary" @click="showCreateModal('team_pro')" :disabled="hasProPlan">
                        Pro Plan erstellen
                      </button>
                      <button class="btn btn-primary" @click="showCreateModal('team_ultimate')" :disabled="hasUltimatePlan">
                        Ultimate Plan erstellen
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="row">
                  <div 
                    v-for="rule in teamRules" 
                    :key="rule.id" 
                    class="col-md-6 mb-3"
                  >
                    <div class="card h-100">
                      <div class="card-header">
                        <h6 class="mb-0">
                          <font-awesome-icon 
                            :icon="rule.type === 'team_pro' ? 'star' : 'crown'" 
                            class="me-2" 
                          />
                          {{ rule.type === 'team_pro' ? 'Pro Plan' : 'Ultimate Plan' }}
                        </h6>
                      </div>
                      <div class="card-body">
                        <div v-if="rule.description" class="mb-3">
                          {{ rule.description }}
                        </div>
                        <div class="row">
                          <div class="col-6">
                            <small class="text-muted">Quittungen pro Monat:</small>
                            <div class="h5 mb-0">{{ rule.teamPlanCoins }}</div>
                          </div>
                          <div class="col-6">
                            <small class="text-muted">Jahrespreis:</small>
                            <div class="h5 mb-0">{{ formatCurrency(rule.teamPlanPrice) }}</div>
                          </div>
                        </div>
                        <div class="mt-2">
                          <span :class="getPriceTypeBadgeClass(rule.teamPlanPriceType)">
                            {{ rule.teamPlanPriceType === 'netto' ? 'Netto' : 'Brutto' }}
                          </span>
                          <span :class="getStatusBadgeClass(rule.isActive)" class="ms-2">
                            {{ rule.isActive ? 'Aktiv' : 'Inaktiv' }}
                          </span>
                        </div>
                        <!-- Feature-Liste -->
                        <div v-if="rule.teamPlanFeatures && rule.teamPlanFeatures.length > 0" class="mt-3">
                          <small class="text-muted d-block mb-2">Features:</small>
                          <ul class="list-unstyled small mb-0">
                            <li v-for="feature in rule.teamPlanFeatures" :key="feature" class="mb-1">
                              <font-awesome-icon icon="check" class="text-success me-1" />
                              {{ feature }}
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div class="card-footer">
                        <div class="btn-group btn-group-sm w-100">
                          <button 
                            class="btn btn-outline-primary" 
                            @click="editRule(rule)"
                          >
                            <font-awesome-icon icon="edit" class="me-1" />
                            Bearbeiten
                          </button>
                          <button 
                            class="btn btn-outline-danger" 
                            @click="deleteRule(rule)"
                          >
                            <font-awesome-icon icon="trash" class="me-1" />
                            Löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Rule Modal -->
    <div 
      v-if="showModal"
      class="modal" 
      id="pricingRuleModal" 
      tabindex="-1" 
      aria-labelledby="pricingRuleModalLabel" 
      aria-hidden="true"
      @hidden.bs.modal="handleModalHidden"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="pricingRuleModalLabel">
              {{ editingRule ? 'Preisregel bearbeiten' : 'Neue Preisregel erstellen' }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeModal" 
              aria-label="Close"
            ></button>
          </div>
          <form @submit.prevent="saveRule">
            <div class="modal-body">
              <div class="row">
                <!-- Regel-Typ -->
                <div class="col-md-6 mb-3">
                  <label for="ruleType" class="form-label">Regel-Typ *</label>
                  <select 
                    id="ruleType" 
                    v-model="form.type" 
                    class="form-select" 
                    required
                    :disabled="editingRule"
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="coin_purchase">Quittung-Kauf</option>
                    <option value="team_pro">Bereich Pro Plan</option>
                    <option value="team_ultimate">Bereich Ultimate Plan</option>
                  </select>
                </div>



                <!-- Beschreibung -->
                <div class="col-12 mb-3">
                  <label for="ruleDescription" class="form-label">Beschreibung</label>
                  <textarea 
                    id="ruleDescription" 
                    v-model="form.description" 
                    class="form-control" 
                    rows="2"
                  ></textarea>
                </div>

                <!-- Quittung-spezifische Felder -->
                <template v-if="form.type === 'coin_purchase'">
                  <div class="col-md-6 mb-3">
                    <label for="minCoins" class="form-label">Minimale Quittungen</label>
                    <input 
                      type="number" 
                      id="minCoins" 
                      v-model.number="form.minCoins" 
                      class="form-control" 
                      min="1"
                    >
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="maxCoins" class="form-label">Maximale Quittungen</label>
                    <input 
                      type="number" 
                      id="maxCoins" 
                      v-model.number="form.maxCoins" 
                      class="form-control" 
                      min="1"
                    >
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="pricePerCoin" class="form-label">Preis pro Quittung (€) *</label>
                    <input 
                      type="number" 
                      id="pricePerCoin" 
                      v-model.number="form.pricePerCoin" 
                      class="form-control" 
                      step="0.01" 
                      min="0.01" 
                      required
                    >
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="priceType" class="form-label">Preis-Typ *</label>
                    <select 
                      id="priceType" 
                      v-model="form.priceType" 
                      class="form-select" 
                      required
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="netto">Netto</option>
                      <option value="brutto">Brutto</option>
                    </select>
                  </div>
                </template>

                <!-- Plan-spezifische Felder -->
                <template v-if="form.type === 'team_pro' || form.type === 'team_ultimate'">
                  <div class="col-md-6 mb-3">
                    <label for="teamPlanCoins" class="form-label">Quittungen pro Monat *</label>
                    <input 
                      type="number" 
                      id="teamPlanCoins" 
                      v-model.number="form.teamPlanCoins" 
                      class="form-control" 
                      min="1" 
                      required
                    >
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="teamPlanPrice" class="form-label">Jahrespreis (€) *</label>
                    <input 
                      type="number" 
                      id="teamPlanPrice" 
                      v-model.number="form.teamPlanPrice" 
                      class="form-control" 
                      step="0.01" 
                      min="0.01" 
                      required
                    >
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="teamPlanPriceType" class="form-label">Preis-Typ *</label>
                    <select 
                      id="teamPlanPriceType" 
                      v-model="form.teamPlanPriceType" 
                      class="form-select" 
                      required
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="netto">Netto</option>
                      <option value="brutto">Brutto</option>
                    </select>
                  </div>
                  <div class="col-12 mb-3">
                    <label for="teamPlanFeatures" class="form-label">Feature-Liste</label>
                    <div class="form-text mb-2">
                      Gib die Features des Plans ein (ein Feature pro Zeile)
                    </div>
                    <textarea 
                      id="teamPlanFeatures" 
                      v-model="form.teamPlanFeaturesText" 
                      class="form-control" 
                      rows="6"
                      placeholder="z.B.&#10;Unbegrenzte Quittungen&#10;Erweiterte Statistiken&#10;Bereich-Management&#10;API-Zugang"
                    ></textarea>
                    <div class="form-text">
                      Leerzeilen werden automatisch entfernt
                    </div>
                  </div>
                </template>

                <!-- Allgemeine Felder -->
                <div class="col-md-6 mb-3">
                  <div class="form-check mt-4">
                    <input 
                      type="checkbox" 
                      id="isActive" 
                      v-model="form.isActive" 
                      class="form-check-input"
                    >
                    <label class="form-check-label" for="isActive">
                      Regel ist aktiv
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button 
                type="button" 
                class="btn btn-secondary" 
                @click="closeModal"
              >
                Abbrechen
              </button>
              <button 
                type="submit" 
                class="btn btn-primary" 
                :disabled="saving"
              >
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                {{ editingRule ? 'Aktualisieren' : 'Erstellen' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { adminPricingApi } from '@/api/pricing'
import { Modal } from 'bootstrap'
import Swal from 'sweetalert2'


const loading = ref(false)
const error = ref(null)
const activeTab = ref('coin')
const rules = ref([])
const editingRule = ref(null)
const saving = ref(false)
const showModal = ref(false)
let modalInstance = null


const form = ref({
  type: '',
  description: '',
  minCoins: null,
  maxCoins: null,
  pricePerCoin: null,
  priceType: '',
  teamPlanCoins: null,
  teamPlanPrice: null,
  teamPlanPriceType: '',
  teamPlanFeatures: [],
  teamPlanFeaturesText: '',
  isActive: true
})


const coinRules = computed(() => {
  return (rules.value || []).filter(rule => rule.type === 'coin_purchase')
})

const teamRules = computed(() => {
  return (rules.value || []).filter(rule => rule.type === 'team_pro' || rule.type === 'team_ultimate')
})

const hasProPlan = computed(() => {
  return teamRules.value.some(rule => rule.type === 'team_pro' && rule.isActive)
})

const hasUltimatePlan = computed(() => {
  return teamRules.value.some(rule => rule.type === 'team_ultimate' && rule.isActive)
})


const loadRules = async () => {
  try {
    loading.value = true
    error.value = null
    const response = await adminPricingApi.getAllPricingRules()

    rules.value = response || []
  } catch (err) {
    error.value = err.response?.data?.message || 'Fehler beim Laden der Preisregeln'
    console.error('Error loading pricing rules:', err)
    rules.value = [] 
  } finally {
    loading.value = false
  }
}

const showCreateModal = (type = null) => {
  editingRule.value = null
  resetForm()
  if (type) {
    form.value.type = type
  }
  
  initializeAndShowModal()
}

const initializeAndShowModal = () => {
  showModal.value = true
  
  
  setTimeout(() => {
    const modalElement = document.getElementById('pricingRuleModal')
    if (modalElement) {
      modalInstance = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      })
      
      modalInstance.show()
    }
  }, 50)
}

const editRule = (rule) => {
  editingRule.value = rule

  const featuresText = rule.teamPlanFeatures && Array.isArray(rule.teamPlanFeatures) 
    ? rule.teamPlanFeatures.join('\n') 
    : ''
  
  form.value = { 
    ...rule,
    teamPlanFeaturesText: featuresText
  }
  
  initializeAndShowModal()
}

const resetForm = () => {
  form.value = {
    type: '',
    description: '',
    minCoins: null,
    maxCoins: null,
    pricePerCoin: null,
    priceType: '',
    teamPlanCoins: null,
    teamPlanPrice: null,
    teamPlanPriceType: '',
    teamPlanFeatures: [],
    teamPlanFeaturesText: '',
    isActive: true
  }
}

const saveRule = async () => {
  try {
    saving.value = true
    

    const dataToSend = { ...form.value }
    if (form.value.teamPlanFeaturesText) {
      dataToSend.teamPlanFeatures = form.value.teamPlanFeaturesText
        .split('\n')
        .map(feature => feature.trim())
        .filter(feature => feature.length > 0)
    } else {
      dataToSend.teamPlanFeatures = []
    }
    

    delete dataToSend.teamPlanFeaturesText
    
    if (editingRule.value) {
      await adminPricingApi.updatePricingRule(editingRule.value.id, dataToSend)
      await Swal.fire({
        icon: 'success',
        title: 'Erfolgreich aktualisiert',
        text: 'Die Preisregel wurde erfolgreich aktualisiert.'
      })
    } else {
      await adminPricingApi.createPricingRule(dataToSend)
      await Swal.fire({
        icon: 'success',
        title: 'Erfolgreich erstellt',
        text: 'Die Preisregel wurde erfolgreich erstellt.'
      })
    }
    

    if (modalInstance) {
      modalInstance.hide()
    }
    
    await loadRules()
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Fehler beim Speichern der Preisregel'
    await Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: errorMessage
    })
  } finally {
    saving.value = false
  }
}

const deleteRule = async (rule) => {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Preisregel löschen?',
    text: `Möchtest du die Preisregel "${rule.name}" wirklich löschen?`,
    showCancelButton: true,
    confirmButtonText: 'Ja, löschen',
    cancelButtonText: 'Abbrechen',
    confirmButtonColor: '#dc3545'
  })
  
  if (result.isConfirmed) {
    try {
      await adminPricingApi.deletePricingRule(rule.id)
      await Swal.fire({
        icon: 'success',
        title: 'Erfolgreich gelöscht',
        text: 'Die Preisregel wurde erfolgreich gelöscht.'
      })
      await loadRules()
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Fehler beim Löschen der Preisregel'
      await Swal.fire({
        icon: 'error',
        title: 'Fehler',
        text: errorMessage
      })
    }
  }
}

const formatPrice = (price) => {
  return parseFloat(price).toFixed(2)
}

const formatCurrency = (price) => {
  const priceInEuro = price ? parseFloat(price) : 0
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(priceInEuro)
}

const getPriceTypeBadgeClass = (priceType) => {
  return priceType === 'netto' ? 'badge bg-info' : 'badge bg-warning'
}

const getStatusBadgeClass = (isActive) => {
  return isActive ? 'badge bg-success' : 'badge bg-secondary'
}


onMounted(() => {
  loadRules()
})


const closeModal = () => {
  if (modalInstance) {
    modalInstance.hide()
  }
}

const handleModalHidden = () => {
  if (modalInstance) {
    modalInstance.dispose()
    modalInstance = null
  }
  showModal.value = false
  resetForm()
}
</script>
